import React, { ComponentProps, ElementType, HTMLAttributes } from 'react'
import { cva, VariantProps } from 'class-variance-authority'

import { IconInfo, IconWarning } from '@/components/svgr/common'
import { cn } from '@/lib/utils'

const textVariants = cva('whitespace-pre-wrap', {
  variants: {
    variant: {
      default: 'text-color-text-primary',
      secondary: 'text-color-text-secondary',
      tertiary: 'text-color-text-tertiary',
      destructive: 'text-destructive',
      warning: 'text-warning'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

export const Text: React.FC<
  { as?: ElementType } & HTMLAttributes<HTMLElement> &
    VariantProps<typeof textVariants>
> = ({ as: Component = 'p', variant, className, ...rest }) => {
  return (
    // @ts-ignore
    <Component className={cn(textVariants({ variant, className }))} {...rest} />
  )
}

export const SilkscreenText: React.FC<ComponentProps<typeof Text>> = ({
  className,
  ...rest
}) => <Text className={cn('silkscreen', className)} {...rest} />

export const OrbitronText: React.FC<ComponentProps<typeof Text>> = ({
  className,
  ...rest
}) => <Text className={cn('font-Orbitron', className)} {...rest} />

export const DINText: React.FC<ComponentProps<typeof Text>> = ({
  className,
  ...rest
}) => <Text className={cn('font-DIN', className)} {...rest} />

export const PartText: React.FC<
  { as?: ElementType } & HTMLAttributes<HTMLElement> &
    VariantProps<typeof textVariants>
> = ({ as: Component = 'p', variant, className, style, ...rest }) => {
  return (
    // @ts-ignore
    <Component
      className={cn('break-words', textVariants({ variant, className }))}
      style={{ wordBreak: 'break-word', ...style }}
      {...rest}
    />
  )
}

const tipsTextVariants = cva('px-4 py-2', {
  variants: {
    variant: {
      info: 'bg-secondary text-primary',
      warning: 'bg-warning-background text-warning',
      destructive: 'bg-destructive-background text-destructive'
    }
  },
  defaultVariants: {
    variant: 'info'
  }
})
export const TipsText: React.FC<
  { as?: ElementType } & HTMLAttributes<HTMLElement> &
    VariantProps<typeof tipsTextVariants> & {
      classNameIcon?: string
      icon?: React.ReactElement | HTMLElement
    }
> = ({ variant, className, icon, classNameIcon, children, ...rest }) => {
  return (
    <PartText
      className={cn(
        'flex items-center',
        tipsTextVariants({ variant, className })
      )}
      {...rest}
    >
      {icon !== undefined ? (
        <>{icon}</>
      ) : variant === 'warning' || variant === 'destructive' ? (
        <IconWarning
          className={cn(
            'mr-2 size-[18px] flex-none text-inherit',
            classNameIcon
          )}
        />
      ) : (
        <IconInfo
          className={cn(
            'mr-2 size-[18px] flex-none text-inherit',
            classNameIcon
          )}
        />
      )}
      {children}
    </PartText>
  )
}

// export const TipsErrorText: React.FC<
//   ComponentProps<typeof Text> & {
//     classNameIcon?: string
//   }
// > = ({ className, classNameIcon, children, ...rest }) => {
//   return (
//     <PartText
//       className={cn(
//         'flex items-center font-light text-destructive bg-[#301B3E] px-3 py-2 opacity-80',
//         className
//       )}
//       variant="tertiary"
//       {...rest}
//     >
//       <Component
//         fill='currentColor'
//         className={cn('mr-2 size-[18px] flex-none', classNameIcon)}
//       />
//       <span>{children}</span>
//     </PartText>
//   )
// }

export const ErrorText: React.FC<ComponentProps<typeof Text>> = ({
  className,
  children,
  ...rest
}) => {
  return (
    <PartText
      className={cn('py-3 text-xs text-destructive', className)}
      variant="tertiary"
      {...rest}
    >
      {children}
    </PartText>
  )
}
